import React from 'react';
import { Button, Form, Row, Col, Dropdown, FloatingLabel, InputGroup } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import { EditEventReference } from 'edit/reference/EditEventReference';

import type { EditableNodeDefinition } from '../../edit/EditableNodeDefinition';
import './RuntimeLimits.scss';

type RuntimeLimitsProps = {
  entity: EditableNodeDefinition;
};

type RuntimeLimitFieldProps = {
  controlId: string;
  label: string;
  fieldName: string;
  value: string;
  helpText: string;
  parameterNames: string[];
  onTextChange: (fieldName: string, value: string) => void;
  onInsertParameter: (fieldName: string, parameterName: string) => void;
  disabled?: boolean;
};

const RuntimeLimitField: React.FC<RuntimeLimitFieldProps> = ({
  controlId,
  label,
  fieldName,
  value,
  helpText,
  parameterNames,
  onTextChange,
  onInsertParameter,
  disabled = false,
}) => {
  const hasValue = value.trim() !== '';

  return (
    <Form.Group className="mb-2">
      <InputGroup>
        <FloatingLabel controlId={controlId} label={label} className="flex-grow-1">
          <Form.Control
            type="text"
            aria-label={label}
            value={value}
            onChange={(e) => onTextChange(fieldName, e.target.value)}
            placeholder={label}
            disabled={disabled}
            spellCheck={false}
          />
        </FloatingLabel>
        <Dropdown align="end">
          <Dropdown.Toggle
            variant="outline-secondary"
            id={`${controlId}-parameters`}
            aria-label={`Insert parameter for ${label}`}
            disabled={disabled || parameterNames.length === 0}
          >
            Parameters
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {parameterNames.map((parameterName) => (
              <Dropdown.Item key={parameterName} onClick={() => onInsertParameter(fieldName, parameterName)}>
                {parameterName}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <Button variant="outline-secondary" aria-label={`Clear ${label}`} onClick={() => onTextChange(fieldName, '')} disabled={disabled || !hasValue}>
          Clear
        </Button>
      </InputGroup>
      <Form.Text className="runtime-limit-help text-muted">
        {helpText} Accepts <code>HH:MM:SS</code> or <code>{'{{parameter_name}}'}</code> or <code>{'={{parameter_name}} * 1000'}</code>.
      </Form.Text>
    </Form.Group>
  );
};

/**
 * The Properties-tab runtime-limit fields, bound to the EditableNodeDefinition model. Min/Max accept an HH:MM:SS literal, a
 * `{{parameter}}` reference, or an `=expression`; the parameter-insert dropdown is fed from the model's own parameters.
 */
const RuntimeLimits = observer(({ entity }: RuntimeLimitsProps) => {
  const parameterNames = Array.from(new Set(entity.parameters.map((parameter) => parameter?.name).filter(Boolean))) as string[];

  const setLimit = (fieldName: string, value: string) => {
    const next = value === '' ? null : value;
    if (fieldName === 'runtime_limit_min_hhmmss') entity.runtimeLimitMinHhmmss = next;
    else if (fieldName === 'runtime_limit_max_hhmmss') entity.runtimeLimitMaxHhmmss = next;
  };
  const insertParameter = (fieldName: string, parameterName: string) => setLimit(fieldName, `{{${parameterName}}}`);

  return (
    <div className="mb-3">
      <h5 className="mb-2">Runtime Limits</h5>
      <Row>
        <Col md={6} xl={3}>
          <RuntimeLimitField
            controlId="form-RuntimeLimitMin"
            label="Minimum"
            fieldName="runtime_limit_min_hhmmss"
            value={entity.runtimeLimitMinHhmmss ?? ''}
            helpText="Fails if job completes faster."
            parameterNames={parameterNames}
            onTextChange={setLimit}
            onInsertParameter={insertParameter}
          />
        </Col>
        <Col md={6} xl={3}>
          <RuntimeLimitField
            controlId="form-RuntimeLimitMax"
            label="Maximum"
            fieldName="runtime_limit_max_hhmmss"
            value={entity.runtimeLimitMaxHhmmss ?? ''}
            helpText="Fails if job runs longer."
            parameterNames={parameterNames}
            onTextChange={setLimit}
            onInsertParameter={insertParameter}
          />
        </Col>
        <Col md={6} xl={3}>
          <Form.Group className="mb-2" controlId="form-RuntimeLimitEvent">
            <EditEventReference label="Event on Exceed" value={entity.runtimeLimitEvent} onChange={(id) => (entity.runtimeLimitEvent = id)} />
          </Form.Group>
        </Col>
        <Col md={6} xl={3}>
          {/* Center the checkbox in a control-height box so it lines up with the sibling inputs, help text below (as they have). */}
          <div className="d-flex align-items-center" style={{ minHeight: '3.5rem' }}>
            <Form.Check
              type="checkbox"
              id="form-RuntimeLimitTerminate"
              label="Kill on exceed"
              checked={entity.runtimeLimitTerminate}
              onChange={(e) => (entity.runtimeLimitTerminate = e.target.checked)}
            />
          </div>
          <Form.Text className="text-muted">Kill job when max runtime exceeded</Form.Text>
        </Col>
      </Row>
    </div>
  );
});

export default RuntimeLimits;
